/**
 * Turn a sheet-scan response into the edits the two school wizards apply
 * (PROD-1279 D26, plan 03 §D.3).
 *
 * Apply rule, both tasks: a non-null extracted value OVERWRITES the current
 * cell, a null KEEPS it — a half-filled sheet tops the grid up rather than
 * wiping it. This is the chef rule (`pages/temperature-log.tsx`), applied to
 * counts as well as temperatures.
 *
 * `applyServiceScan` returns PATCHES, never a merged `ProductionEdits`. The
 * complete-service flow's `updateRow` owns the two count identities, the
 * manual-field bookkeeping and the violation state; writing edit state directly
 * would silently un-derive the grid. Patch order per row is fixed
 * (`served, reimbursable, nonReimbursable, leftover`) so derivation runs
 * identically every time.
 *
 * A value the grid ALREADY holds is counted as filled but not patched: routing
 * it through `updateRow` would mark that field manual and freeze the row's
 * derivation for a number the scan did not actually change.
 *
 * Pure — no React, no fetch.
 */

import type { ScanSheetResponse } from '@/api/sheet-scan'
import type { ProductionEdits } from '@/lib/cafeteria/format'
import type { CountField } from '@/lib/cafeteria/production-counts'
import type { ServiceScanTarget, TransportScanTarget } from '@/lib/sheet-scan/build-request'

/** A cell the model filled but was unsure about — the grid rings it amber (contract E5). */
export interface ScanCellFlag {
  rowKey: string
  field: string
}

/** The part of the card's summary a scan result determines on its own. */
export interface ScanPartialSummary {
  /** Cells the photos actually filled. */
  filledCount: number
  /** Filled cells the model was unsure about. */
  lowConfidenceCount: number
  /** One line per rejected photo, already phrased for display. */
  warnings: string[]
  /** Item names read off the paper that we could not place. */
  unmatchedRowsSeen: string[]
}

/** Per-line temperature provenance for the transport submit (`deliverySource` / `deliveryConfidence`). */
export type ScanProvenance = Record<string, { source: 'PHOTO'; confidence: 'high' | 'low' }>

/** The four count columns, in the order derivation must see them. */
const COUNT_FIELDS: readonly CountField[] = ['served', 'reimbursable', 'nonReimbursable', 'leftover']

export interface TransportScanApplication {
  /** ONLY the counts the photos filled, keyed by row. Merge over the live map. */
  qtyPatch: Record<string, number>
  /** ONLY the readings the photos filled, keyed by row or milk group. */
  tempPatch: Record<string, number>
  provenance: ScanProvenance
  flags: ScanCellFlag[]
  summary: ScanPartialSummary
}

/**
 * PATCHES, never a merged map. A scan can take two minutes on a cafeteria
 * connection and the grid stays live throughout, so a whole-map replace built
 * from the pre-scan snapshot silently discards everything the worker typed
 * while the spinner ran. Merging these two objects over the LATEST state, in a
 * functional setState, keeps both. A cell the photos did not fill is simply
 * absent, which is also the "a null KEEPS what is there" rule.
 */
export function applyTransportScan(
  result: ScanSheetResponse,
  index: ReadonlyMap<string, TransportScanTarget>,
): TransportScanApplication {
  const qtyPatch: Record<string, number> = {}
  const tempPatch: Record<string, number> = {}
  const provenance: ScanProvenance = {}
  const flags: ScanCellFlag[] = []
  let filledCount = 0
  let lowConfidenceCount = 0

  for (const row of result.rows) {
    const target = index.get(row.rowId)
    if (!target) {
      continue
    }
    const received = row.cells['receivedQuantity']
    if (received && received.value !== null) {
      qtyPatch[target.key] = received.value
      filledCount += 1
      if (received.confidence === 'low') {
        lowConfidenceCount += 1
        flags.push({ rowKey: target.key, field: 'receivedQuantity' })
      }
    }
    const dropOff = row.cells['dropOffTemperatureF']
    // A shelf-stable row takes no reading: the printed sheet still boxes the
    // cell and the extractor still answers for it, but writing that number
    // would put an invented temperature on a HACCP record in a cell the grid
    // renders as `N/A — room temp` — no control, no ring, nothing to clear.
    if (target.tempOffered && dropOff && dropOff.value !== null) {
      tempPatch[target.key] = dropOff.value
      provenance[target.key] = { source: 'PHOTO', confidence: dropOff.confidence }
      filledCount += 1
      if (dropOff.confidence === 'low') {
        lowConfidenceCount += 1
        flags.push({ rowKey: target.key, field: 'dropOffTemperatureF' })
      }
    }
  }

  return {
    qtyPatch,
    tempPatch,
    provenance,
    flags,
    summary: {
      filledCount,
      lowConfidenceCount,
      warnings: buildPhotoWarnings(result.images),
      unmatchedRowsSeen: result.unmatchedRowsSeen,
    },
  }
}

export interface ServiceCountPatch {
  prId: string
  rowKey: string
  field: CountField
  value: number
  /** Provisioned count for the row — `updateRow`'s fourth argument. */
  planned: number
}

export interface ServiceTempPatch {
  prId: string
  deliverableKey: string
  value: number
}

export interface ServiceScanApplication {
  patches: ServiceCountPatch[]
  temps: ServiceTempPatch[]
  flags: ScanCellFlag[]
  summary: ScanPartialSummary
}

export function applyServiceScan(
  result: ScanSheetResponse,
  index: ReadonlyMap<string, ServiceScanTarget>,
  current: ProductionEdits,
): ServiceScanApplication {
  const patches: ServiceCountPatch[] = []
  const temps: ServiceTempPatch[] = []
  const flags: ScanCellFlag[] = []
  let filledCount = 0
  let lowConfidenceCount = 0

  for (const row of result.rows) {
    const target = index.get(row.rowId)
    if (!target) {
      continue
    }
    const editedRow = current[target.productionRecordId]?.rows[target.rowKey]

    for (const field of COUNT_FIELDS) {
      const cell = row.cells[field]
      if (!cell || cell.value === null) {
        continue
      }
      filledCount += 1
      if (cell.confidence === 'low') {
        lowConfidenceCount += 1
        flags.push({ rowKey: target.rowKey, field })
      }
      if (editedRow?.[field] === cell.value) {
        continue
      }
      patches.push({
        prId: target.productionRecordId,
        rowKey: target.rowKey,
        field,
        value: cell.value,
        planned: target.planned,
      })
    }

    // The deliverable's single serving temperature hangs off its item row; a
    // grade sub-row never carries one (the server drops it, and so do we).
    const serving = row.cells['servingTemperatureF']
    if (target.isItemRow && serving && serving.value !== null) {
      filledCount += 1
      if (serving.confidence === 'low') {
        lowConfidenceCount += 1
        flags.push({ rowKey: target.deliverableKey, field: 'servingTemperatureF' })
      }
      if (current[target.productionRecordId]?.temps[target.deliverableKey] !== serving.value) {
        temps.push({
          prId: target.productionRecordId,
          deliverableKey: target.deliverableKey,
          value: serving.value,
        })
      }
    }
  }

  return {
    patches,
    temps,
    flags,
    summary: {
      filledCount,
      lowConfidenceCount,
      warnings: buildPhotoWarnings(result.images),
      unmatchedRowsSeen: result.unmatchedRowsSeen,
    },
  }
}

/**
 * Re-number a response's `images` by the positions the photos held ON THE
 * DEVICE.
 *
 * `filesToImagePayloads` drops a photo it cannot send (unsupported format, too
 * large) and the server indexes `images[]` over what it RECEIVED, so with photo
 * 2 of 3 rejected the server's index 1 is the worker's photo 3. Every warning
 * that names a "Photo N" would then send them back to re-shoot the wrong page.
 * `sourceIndices[i]` is the device position of the i-th SENT photo.
 *
 * Applied to the whole response (not just the warnings) so nothing downstream
 * can read a wire index and mean a device index by it.
 */
export function remapPhotoIndices(result: ScanSheetResponse, sourceIndices: readonly number[]): ScanSheetResponse {
  return {
    ...result,
    images: result.images.map((image) => ({ ...image, index: sourceIndices[image.index] ?? image.index })),
  }
}

/**
 * One warning line per photo the extractor judged to be a different sheet. The
 * other photos still applied — a rejected page never fails the batch (D26).
 */
export function buildPhotoWarnings(images: ScanSheetResponse['images']): string[] {
  const warnings: string[] = []
  for (const image of images) {
    if (image.verification.matchesExpected) {
      continue
    }
    const observed = [image.verification.observedLocationText, image.verification.observedDateText]
      .filter((part): part is string => Boolean(part && part.trim()))
      .join(', ')
    const reason = image.verification.reason?.trim() || observed || 'the school or date on it does not match'
    warnings.push(`Photo ${image.index + 1} looks like a different sheet: ${reason}`)
  }
  return warnings
}

/**
 * The client aborts a scan at `SCAN_TIMEOUT_MS` (`api/sheet-scan.ts`), and that
 * abort rejects with a `DOMException` named `TimeoutError` — NOT an `ApiError`.
 * A wizard that only understands `ApiError` would show the generic "could not
 * read the photos" line for what is really a slow cafeteria connection, so both
 * wizards translate the abort with this pair. `name` is read off the value
 * rather than tested with `instanceof Error`, because `DOMException` only
 * joined the `Error` hierarchy recently and a tablet browser may predate that.
 */
export const SCAN_TIMEOUT_MESSAGE =
  'Reading the photos took too long. Try again with fewer photos, or enter the numbers by hand.'

/** True for the timeout above, and for any other caller-side abort. */
export function isScanAbort(err: unknown): boolean {
  const name = (err as { name?: unknown } | null | undefined)?.name
  return name === 'TimeoutError' || name === 'AbortError'
}

/**
 * The sheet's "Time reading" box, normalised to the 24-hour `HH:MM` an
 * `<input type="time">` accepts. Tolerates surrounding text ("Time: 7:05am"), a
 * dot separator, a missing separator ("1045") and either meridiem spelling.
 * Returns null for anything out of range or unparseable — the input is then
 * left for the worker.
 *
 * TWO patterns, tried in order, NOT one with an optional separator. A separated
 * time is unambiguous, so it wins wherever it appears; the bare digit run is a
 * fallback for a box that really does read "1045". With one optional-separator
 * pattern, "9/17 10:45" matched the DATE first (17 as the hour, 10 as the
 * minutes → "17:10") and that fiction went straight into `arrivedAt`.
 *
 * The digit boundaries matter in both: without them "123456" would parse as
 * 12:34.
 */
const TIME_READING_SEPARATED = /(?<!\d)(\d{1,2})\s*[:.]\s*(\d{2})(?!\d)\s*(?:([ap])\.?\s*m\.?)?/i
const TIME_READING_BARE = /(?<!\d)(\d{3,4})(?!\d)\s*(?:([ap])\.?\s*m\.?)?/i

export function parseTimeReading(text: string | null): string | null {
  if (!text) {
    return null
  }
  return readSeparatedTime(text) ?? readBareTime(text)
}

/** "10:45", "10.45", "Time: 7:05am" — a separator is proof it is a clock time. */
function readSeparatedTime(text: string): string | null {
  const match = TIME_READING_SEPARATED.exec(text)
  return match ? normaliseTime(Number(match[1]), Number(match[2]), match[3]) : null
}

/** "1045" — only reached when nothing in the text carries a separator. */
function readBareTime(text: string): string | null {
  const match = TIME_READING_BARE.exec(text)
  if (!match) {
    return null
  }
  const digits = match[1] ?? ''
  return normaliseTime(Number(digits.slice(0, -2)), Number(digits.slice(-2)), match[2])
}

function normaliseTime(rawHour: number, minute: number, rawMeridiem: string | undefined): string | null {
  const meridiem = rawMeridiem?.toLowerCase() ?? null
  if (!Number.isInteger(rawHour) || !Number.isInteger(minute) || minute > 59) {
    return null
  }
  let hour = rawHour
  if (meridiem) {
    // A meridiem only makes sense on a 12-hour clock; "13:45 PM" is garbage,
    // not a 13:45 we should silently accept.
    if (rawHour < 1 || rawHour > 12) {
      return null
    }
    hour = rawHour % 12
    if (meridiem === 'p') {
      hour += 12
    }
  } else if (rawHour > 23) {
    return null
  }
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}
